import { Link, NavLink } from 'react-router-dom'
import { useReactiveVar, useMutation } from '@apollo/client/react'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Container from 'react-bootstrap/Container'
import Nav from 'react-bootstrap/Nav'
import Navbar from 'react-bootstrap/Navbar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock } from '@fortawesome/pro-solid-svg-icons'
import { user, isLoggingOut } from '@cache'
import User from '@utils/storage/user'
import toast from '@utils/toast'
import redirect from '@utils/redirect'
import LOGOUT from '@graphql/mutations/logout'
import { t } from '@utils/translations'

const ADMIN_SIGN_OUT_PATH = '/admin_users/sign_out'

function Navigation() {
  const currentUser = useReactiveVar(user)

  const [logout] = useMutation(LOGOUT)

  // Build the Admin PCI front-channel logout URL. We reuse the OAuth host from
  // Settings and swap the path to the Admin sign-out endpoint.
  const buildAdminLogoutUrl = () => {
    const logoutUrl = new URL(Settings.OAUTH_AUTHENTICATION_URL)
    logoutUrl.pathname = ADMIN_SIGN_OUT_PATH

    return logoutUrl.toString()
  }

  const handleLogout = async () => {
    isLoggingOut(true)

    try {
      // Clears the BSP Processor-side session cookie on the backend.
      await logout()
    } catch (error) {
      toast(error?.message || t('messages.logoutError'), 'danger')
      isLoggingOut(false)
      return
    }

    sessionStorage.removeItem('codeVerifier')
    User.clear()

    // Front-channel logout: navigate the browser (top-level) to the Admin PCI
    // sign-out endpoint. Unlike a cross-origin background request, a top-level
    // navigation is first-party to Admin, so the Admin session cookie is sent
    // and Warden is actually signed out. Admin then revokes the SSO tokens and
    // redirects the browser to its own login screen. This terminates the SSO
    // session so `/oauth/authorize` no longer silently re-issues a code, which
    // is what previously caused the immediate re-login.
    redirect(buildAdminLogoutUrl())
  }

  if (!currentUser) return null

  return (
    <Navbar aria-label='Main navigation' expand='lg' bg='dark' variant='dark'>
      <Container fluid='xl'>
        <Navbar.Brand as={Link} to='/'>BSP Processor</Navbar.Brand>
        <Navbar.Toggle aria-controls='responsive-navbar-nav' />
        <Navbar.Collapse id='responsive-navbar-nav'>
          <Nav as='ul' className='me-auto'>
            <Nav.Item as='li'><NavLink to='/transactions' className='nav-link'>Transactions</NavLink></Nav.Item>
            <Nav.Item as='li'><NavLink to='/merchants' className='nav-link'>Merchants</NavLink></Nav.Item>
            <Nav.Item as='li'><NavLink to='/flights' className='nav-link'>Flights</NavLink></Nav.Item>
            <Nav.Item as='li'><NavLink to='/invoices' className='nav-link'>Invoices</NavLink></Nav.Item>
            <Nav.Item as='li'><NavLink to='/miscellaneous-details' className='nav-link'>Details</NavLink></Nav.Item>
            <Nav.Item as='li'><NavLink to='/miscellaneous-taxes' className='nav-link'>Taxes</NavLink></Nav.Item>
            <Nav.Item as='li'><NavLink to='/payment-plans' className='nav-link'>Payment Plans</NavLink></Nav.Item>
            <Nav.Item as='li'><NavLink to='/transaction-batches' className='nav-link'>Transaction Batches</NavLink></Nav.Item>
          </Nav>
          <Navbar.Text>
            <Button className='logout' variant='link' onClick={handleLogout}>
              <FontAwesomeIcon icon={faLock} className='me-1' />
              Logout <Badge bg='primary'>{currentUser.login}</Badge>
            </Button>
          </Navbar.Text>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  )
}

export default Navigation
